<template>
  <div class="wishlist-modal">
    <CustomModal :show="show"
                 @close="onClose">
      <template #header>
        <h1 class="custom-modal__heading">
          Choose a wish list
        </h1>
      </template>
      <div class="wishlist-modal__body">
        <template v-if="showLoginPrompt || hasLoggedIn">
          <h2 class="product-pricing-modal__heading">
            Log in
          </h2>

          <p class="product-pricing-modal__text">
            To add a product to a wish list, please log in.
          </p>

          <slot name="login"
                :onLogin="onLogin"
                :redirectUrl="redirectUrl">
            <login-form redirect
                        :redirect-url="redirectUrl"
                        @loggedIn="onLogin"></login-form>
          </slot>
        </template>

        <template v-else>
          <template v-if="loading">
            <div class="wishlist-modal__loading-container">
              <div class="wishlist-modal__loading-content">
                <h5>Loading wish lists</h5>
                <font-awesome-icon icon="spinner"
                                   class="icon"
                                   size="lg"
                                   spin></font-awesome-icon>
              </div>
            </div>
          </template>
          <template v-else>
            <div v-if="wishlists && wishlists.length > 0">
              <div class="wishlist-modal__items-container">
                <div v-for="(item, index) in wishlists"
                     :key="index"
                     class="wishlist-modal__item">
                  <span>{{ renderWishlistTitle(item) }}</span>
                  <button
                    class="btn btn-accent-1 btn-sm wishlist-modal__action-button"
                    :disabled="item.submitting"
                    @click="onActionButtonClick(item)">
                    <template v-if="item.submitting">
                      <span class="visually-hidden">
                        Loading...
                      </span>
                      <font-awesome-icon icon="spinner"
                                         spin></font-awesome-icon>
                    </template>
                    <template v-else>
                      {{ renderActionButtonText(item) }}
                    </template>
                  </button>
                </div>
              </div>
            </div>
            <div v-else>
              No wish lists found, why not create one?
            </div>

            <div class="wishlist-modal__create-new">
              <div class="form-group">
                <input
                  v-model="newWishlistName"
                  class="form-control"
                  type="text"
                  name="wishlist-name"
                  placeholder="New wish list name"
                  aria-label="Wishlist">
              </div>
              <button
                class="btn btn-accent-6 create-btn"
                :disabled="!newWishlistName || newWishlistName.length === 0"
                @click="onCreateClick">
                <template v-if="creatingNew">
                  <span class="visually-hidden">
                    Creating wish list, please wait...
                  </span>
                  <font-awesome-icon icon="spinner"
                                     spin></font-awesome-icon>
                </template>
                <template v-else>
                  Create
                </template>
              </button>
            </div>
            <span v-if="errorMessage"
                  class="wishlist-modal__create-new-error">
              {{ errorMessage }}
            </span>
          </template>
        </template>
      </div>

      <!-- Added successfully modal -->
      <CustomModal :show="showAddedSuccessfully"
                   size="xs"
                   @close="showAddedSuccessfully = false">
        <template #header>
          <h1 class="custom-modal__heading">
            Wish list
          </h1>
        </template>
        <template>
          This product has been added to the wish list.
        </template>
        <template #footer>
          <button class="btn btn-accent-6"
                  @click="showAddedSuccessfully = false">
            Close
          </button>
        </template>
      </CustomModal>
    </CustomModal>
  </div>
</template>

<script>
/* eslint-disable max-len */
// import { BasketResource } from "spiritweb.packages.ecommerce";
import CustomModal from "~/Components/CustomModal.vue";
import LoginForm from "~/Components/Accounts/LoginForm.vue";
import { transferOrderLines, transferOrderLineModel } from "~/Scripts/resources/ordersResource";

import {
  listWishlists,
  createWishlist,
  addToWishlist,
  bulkAddToWishlist,
  removeFromWishlist
} from "~/Scripts/resources/wishlistResource";

export const basket = {
  setBasketItemQuantity: {
    REQUEST: "sw::setBasketItemQuantity::request",
    RESPONSE: "sw::setBasketItemQuantity::response",
    SUCCESS: "sw::setBasketItemQuantity::success",
    FAILURE: "sw::setBasketItemQuantity::failure",
    START: "sw::setBasketItemQuantity::start",
    STOP: "sw::setBasketItemQuantity::stop",
    UPDATE: "sw::setBasketItemQuantity::update"
  }
};

export default {
  name: "WishlistModal",
  components: {
    CustomModal,
    LoginForm
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    showLoginPrompt: {
      type: Boolean,
      default: true
    },
    productId: {
      type: Number,
      default: null
    },
    pricingScheduleId: {
      type: Number,
      default: null
    },
    productFormatId: {
      type: Number,
      default: 0
    },
    productLicenceTypeId: {
      type: Number,
      default: 0
    },
    bulkProducts: {
      type: Array,
      default: null
    },
    removeOrderLineId: {
      type: String,
      default: null
    },
    transferOrderLine: {
      type: Object,
      default: () => ( {
        orderHeaderId: null,
        orderLineId: null
      } ),
      validator ( value ) {
        return (
          Object.prototype.hasOwnProperty.call( value, "orderHeaderId" )
          && Object.prototype.hasOwnProperty.call( value, "orderLineId" )
        );
      }
    }
  },
  data () {
    return {
      wishlists: [],
      newWishlistName: null,
      loading: false,
      creatingNew: false,
      redirectUrl: null,
      errorMessage: null,
      hasLoggedIn: false,
      requesting: false,
      showAddedSuccessfully: false
    };
  },
  watch: {
    show ( val ) {
      this.errorMessage = null;
      this.newWishlistName = null;
      this.wishlists = [];
      if ( val ) {
        this.load();
      }
    }
  },
  created () {
    this.redirectUrl = window.location.href;
  },
  methods: {
    load () {
      this.loading = true;
      listWishlists( false )
        .then( result => {
          if ( result.data ) {
            this.wishlists = result.data;
          }
        } )
        .catch( error => {
          console.log( "error loading wishlists" );
          throw error;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    onActionButtonClick ( wishlist ) {
      if ( wishlist.submitting ) {
        console.warn( "The previous action is busy processing, please wait" );
        return;
      }

      if ( this.$listeners.requestSubmit ) {
        new Promise( ( resolve, reject ) => {
          const data = {
            id: wishlist.id
          };
          this.$emit( "requestSubmit", { data, resolve, reject } );
        } ).catch( () => {} );
      } else {
        const shouldRemove = this.productExistsInWishlist( wishlist );

        this.$set( wishlist, "submitting", true );

        // If the product already exists remove it
        if ( shouldRemove ) {
          this.removeProductFromWishlist( wishlist );
          return;
        }

        // If there are bulk products add them
        if ( this.bulkProducts ) {
          this.addBulkProductsToWishlist( wishlist );
          return;
        }

        // If transfer order line has value, move orderline to wishlist
        const { orderHeaderId, orderLineId } = this.transferOrderLine || {};
        if ( orderHeaderId && orderLineId ) {
          this.transferProductToWishList( wishlist );
          return;
        }

        // else add product to wishlist
        this.addProductToWishlist( wishlist );
      }
    },
    removeProductFromWishlist ( wishlist ) {
      const orderLine = this.getOrderLine( wishlist );

      removeFromWishlist( wishlist.id, orderLine.id )
        .then( result => {
          if ( !result && !result.data ) {
            return;
          }

          wishlist.sopLines = wishlist.sopLines.filter(
            l => l.productId !== orderLine.productId
              && l.pricingScheduleId !== orderLine.pricingScheduleId
          );
        } )
        .catch( error => {
          console.log( "error when removing product from wishlist", error );
          throw error;
        } )
        .finally( () => {
          wishlist.submitting = false;
        } );
    },
    addBulkProductsToWishlist ( wishlist ) {
      bulkAddToWishlist( wishlist.id, this.bulkProducts )
        .then( result => {
          if ( !result && !result.data ) {
            return;
          }

          result.data.forEach( r => {
            if ( r.errorCode === 100 ) {
              const matchingProduct = this.bulkProducts.find( p => p.id === r.productId );
              let scheduleId = 0;

              if ( matchingProduct && matchingProduct !== undefined && matchingProduct.options ) {
                scheduleId = matchingProduct.options.pricingScheduleId;
              }

              wishlist.sopLines.push( {
                id: r.orderLineId,
                productId: r.productId,
                pricingScheduleId: scheduleId
              } );
            }
          } );
        } )
        .catch( error => {
          console.warn( "error when adding products in bulk to wishlist", error );
          throw error;
        } )
        .finally( () => {
          wishlist.submitting = false;
        } );
    },
    addProductToWishlist ( wishlist ) {
      addToWishlist(
        wishlist.id,
        this.productId,
        this.pricingScheduleId,
        this.productFormatId,
        this.productLicenceTypeId,
        1
      )
        .then( result => {
          if ( !result && !result.data ) {
            return;
          }

          this.showAddedSuccessfully = true;

          wishlist.sopLines.push( {
            id: result.data,
            productId: this.productId,
            pricingScheduleId: this.pricingScheduleId,
            productFormatId: this.productFormatId,
            productLicenceTypeId: this.productLicenceTypeId
          } );
        } )
        .catch( error => {
          console.log( "error adding product to wishlist", error );
          throw error;
        } )
        .finally( () => {
          wishlist.submitting = false;
        } );
    },
    transferProductToWishList ( wishlist ) {
      this.requesting = true;

      this.transferOrderLineToWishList( wishlist )
        .then( result => {
          const { data: transferResults } = result || {};
          this.handleTransferResponse( transferResults, wishlist );
        } )
        .catch( err => {
          console.error( "error adding product to wishlist", err );
          throw err;
        } )
        .finally( () => {
          wishlist.submitting = false;
          this.requesting = false;
          this.onClose();
        } );
    },
    transferOrderLineToWishList ( wishlist ) {
      const { id: wishListId } = wishlist || {};
      const { orderHeaderId, orderLineId: id } = this.transferOrderLine || {};

      const transferableOrderLine = transferOrderLineModel( id, "WISHLIST", false );

      return new Promise( ( resolve, reject ) => {
        transferOrderLines( orderHeaderId, wishListId, [transferableOrderLine] )
          .then( res => resolve( res ) )
          .catch( ex => reject( ex ) );
      } );
    },
    onCreateClick () {
      const nameExists = this.wishlists && this.wishlists.find( w => w.title === this.newWishlistName );
      if ( nameExists ) {
        this.errorMessage = "A wish list with that name already exists!";
      } else {
        this.creatingNew = true;
        this.errorMessage = null;
        createWishlist( this.newWishlistName )
          .then( result => {
            if ( result && result.data ) {
              this.wishlists.push( {
                title: this.newWishlistName,
                id: result.data,
                sopLines: []
              } );
              this.newWishlistName = null;
            }
          } )
          .catch( error => {
            console.log( "error saving new wishlist", error );
            throw error;
          } )
          .finally( () => {
            this.creatingNew = false;
          } );
      }
    },
    onClose () {
      this.$emit( "close" );
    },
    onLogin () {
      this.hasLoggedIn = true;
      this.$emit( "login" );
    },
    renderWishlistTitle ( wishlist ) {
      let result = wishlist.title;
      let items = 0;

      if ( wishlist.sopLines ) {
        items = wishlist.sopLines.length;
      }

      if ( items > 0 ) {
        result += ` (${items})`;
      } else {
        result += " (0)";
      }

      return result;
    },
    renderActionButtonText ( wishlist ) {
      let result = "Add to wish list";
      if ( this.productExistsInWishlist( wishlist ) ) {
        result = "Remove from wish list";
      }
      return result;
    },
    getOrderLine ( wishlist ) {
      let result = null;

      if ( wishlist.sopLines && wishlist.sopLines.length > 0 ) {
        result = wishlist.sopLines.find(
          l => l.productId === this.productId
            && l.pricingScheduleId === this.pricingScheduleId
            && l.productFormatId === this.productFormatId
            && l.productLicenceTypeId === this.productLicenceTypeId
        );

        if ( result === undefined ) {
          result = null;
        }
      }

      return result;
    },
    productExistsInWishlist ( wishlist ) {
      return this.getOrderLine( wishlist ) !== null;
    },

    handleTransferResponse ( response, wishList ) {
      const successfulProducts = [];
      const errors = {
        products: {
          fullyBooked: [],
          unavailable: [],
          disallowed: [],
          failed: []
        }
      };

      // map results
      response.map( transferResult => {
        const {
          // bookingTransferResult,
          // removedOrderLineId,
          orderLineTransferResult: {
            errorCode,
            errorDescription,
            orderLineId,
            productId,
            pricingScheduleId
          }
        } = transferResult || {
          orderLineTransferResult: {}
        };

        const { productDisplayName } = wishList.sopLines.find( p => p.productId === productId ) || {};

        switch ( errorCode ) {
          case 108:
          case 111: {
            errors.products.fullyBooked.push( {
              productId,
              name: productDisplayName
            } );
            break;
          }
          case 101:
          case 103: // can't modify a completed wishlit
          case 121: {
            errors.products.unavailable.push( {
              productId,
              name: productDisplayName
            } );
            break;
          }
          case 0:
          case 200: {
            successfulProducts.push( transferResult );

            const mutableWishList = { ...wishList };

            mutableWishList.sopLines.push( {
              id: orderLineId,
              productId,
              pricingScheduleId
            } );

            const mutableWishLists = [...this.wishlists];
            const wishListIndex = mutableWishLists.findIndex( w => w.id === wishList.id );

            mutableWishLists.splice( wishListIndex, 1, mutableWishList );

            this.wishlists = mutableWishLists;
            break;
          }
          case 401: {
            errors.products.disallowed.push( {
              productId,
              name: productDisplayName
            } );
            break;
          }
          case 500:
          default: {
            errors.products.failed.push( {
              productId,
              name: productDisplayName
            } );
            const error = `Handle status code: ${errorCode}: ${errorDescription}`;
            console.warn( error );
            break;
          }
        }

        return productId;
      } );

      // set failed products
      const failedProducts = Object.keys( errors.products ).reduce(
        ( prevKey, currKey ) => ( errors.products[prevKey] || [] ).concat( errors.products[currKey] || [] ),
        []
      );

      if ( failedProducts.length > 0 ) {
        this.onTransferFail( errors );
      }
      if ( successfulProducts.length > 0 ) {
        this.onTransferSuccess( successfulProducts );
      }
    },
    onTransferFail ( failedProducts ) {
      this.$emit( "failed-transfers", failedProducts );
    },
    onTransferSuccess ( succussfulProducts ) {
      succussfulProducts.forEach( result => {
        const { orderLineTransferResult } = result;
        if ( orderLineTransferResult ) {
          this.updateMiniBasketQuantity( orderLineTransferResult );
        }
      } );

      this.$emit( "succussful-transfers", succussfulProducts );
    },
    updateMiniBasketQuantity ( orderLineTransferResult, quantity = 0 ) {
      return new Promise( ( resolve, reject ) => {
        if ( orderLineTransferResult && orderLineTransferResult.orderLineId ) {
          const {
            orderLineId,
            productFormatId,
            pricingScheduleId,
            productLicenceTypeId
          } = orderLineTransferResult;

          const payload = {
            orderLineId,
            quantity,
            pricingScheduleId,
            productFormatId,
            productLicenceTypeId
          };

          this.$root.$emit( basket.setBasketItemQuantity.START );
          this.$root.$emit( basket.setBasketItemQuantity.UPDATE, payload );
          this.$root.$emit( basket.setBasketItemQuantity.RESPONSE );
          this.$root.$emit( basket.setBasketItemQuantity.STOP );

          resolve();
        } else {
          reject( new Error( "A valid Orderline ID is required." ) );
        }
      } );
    }
  }
};
</script>

<style lang="less" scoped>
.wishlist-modal {
  &__body {
    position: relative;
  }

  &__items-container {
    max-height: 300px;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px;
  }

  &__action-button {
    width: 192px;
  }

  &__loading-container {
    display: flex;
    justify-content: center;
  }

  &__loading-content {
    display: flex;
    align-items: center;

    h5 {
      margin-bottom: 0;
    }

    .icon {
      margin: 10px;
    }
  }

  &__create-new {
    display: flex;
    margin-top: 2rem;
    max-width: 400px;
    .form-group {
      width: 100%;
      margin-bottom: 0;
    }

    .create-btn {
      margin-left: 10px;
    }
  }

  &__create-new-error {
    color: #dd4242;
  }
}
</style>
